import React from "react";
import PropTypes from "prop-types";
import EmptyValue from "./EmptyValue";

// Generic, presentational data table. `columns` describes how to read and
// render each cell — loosely borrowing the {Header, accessor} column-shape
// idea from the react-table ecosystem (not the library itself, which is not
// a dependency here). `rows` is plain data; this component has no idea what
// domain it belongs to.
//
// Every class string below is copied VERBATIM from the legacy order table
// (`src/components/orderTable/table-layout.jsx:246-401` — card, header <tr>, <th>, body
// <tr>, <td>) so this table and the reused legacy screens render as one visual language.
// `text-black` on the <td> is the one addition: legacy gets black by inheriting the UA
// default, which `.ops-v2` no longer hands down. Do not hand-convert any of
// them to px: `src/index.scss` sets `* { font-size: 13px }`, which also lands on `html`,
// so `1rem = 13px` on BOTH the legacy routes and `/v2/*`. Every rem-based utility drifts
// identically on both — which means copying the legacy class wholesale is exactly what
// makes the geometry byte-identical, and "correcting" it to an arbitrary px value is what
// would break the match. `py-4` on the cells is legacy's own cell padding, kept as-is
// (13px, not the nominal 16px) for precisely that reason.
//
// Deliberately NOT sticky — no sticky thead, no frozen columns. Legacy has neither, and
// two things now make sticky cells actively unsafe here:
//   1. dropping `border-separate` gives the table `border-collapse: collapse`, under which
//      `position: sticky` on a <th>/<td> is unreliable (Chrome drops the collapsed header
//      border when the thead is stuck), and
//   2. body rows now carry no background of their own (legacy rows are transparent over
//      the white card), so the `bg-[inherit]` trick a frozen column needs in order to
//      occlude scrolled-under text would inherit `transparent` and let the text through.
// Reinstating either feature means reinstating `border-separate border-spacing-0` and an
// opaque row background first — i.e. giving up the legacy match. See §4.3 of
// docs/superpowers/plans/2026-08-28-tat-legacy-restyle-spec.md.

function DataTable({ columns, rows, rowClassName, onRowClick, emptyCell }) {
    const alignClass = (align) => {
        if (align === "right") return "text-right";
        if (align === "center") return "text-center";
        return "text-left";
    };

    return (
        <div className="block w-full overflow-x-auto py-4 bg-[white] rounded-2xl shadow-soft-xl mt-4">
            {/* `w-max min-w-full` rather than legacy's `w-[100%]`: TAT's interval columns are
                derived from the API and can exceed the viewport, so the table is allowed to
                grow past the card and scroll inside the card's own overflow-x. */}
            <table className="table-auto w-max min-w-full font-sans">
                <thead>
                    <tr className="text-left h-16 font-sans outline-1 border-b-2 border-gray-100">
                        {columns.map((col) => (
                            // No size/weight/colour here on purpose: 13px comes from the global
                            // `*` rule, bold from the UA <th> rule (preflight does not reset it),
                            // and black by inheritance — exactly as on the legacy tables.
                            <th
                                key={col.key}
                                className={`whitespace-nowrap pl-3 px-2 font-sans text-left ${alignClass(col.align)}`}
                            >
                                {col.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, rowIndex) => (
                        <tr
                            // eslint-disable-next-line react/no-array-index-key
                            key={row.id ?? rowIndex}
                            onClick={onRowClick ? () => onRowClick(row) : undefined}
                            className={`text-left border-t border-gray-100 h-14 font-sans hover:bg-slate-200 ${
                                onRowClick ? "cursor-pointer" : ""
                            } ${rowClassName ? rowClassName(row) : ""}`}
                        >
                            {columns.map((col) => (
                                // `tabular-nums` is the one non-legacy utility in this file, and it
                                // is here to replace something legacy never had: the duration columns
                                // used to be monospace, which this restyle drops (legacy has no
                                // monospace anywhere, and `font-mono` emits nothing in this config).
                                // Tabular figures keep `2d 4h` / `18h 40m` digit-aligned in Poppins
                                // and are invisible everywhere else.
                                <td
                                    key={col.key}
                                    className={`pl-3 py-4 text-left font-sans tabular-nums text-black ${alignClass(
                                        col.align
                                    )} ${col.wrap ? "" : "whitespace-nowrap"}`}
                                >
                                    {col.render ? col.render(row, rowIndex) : row[col.key] ?? emptyCell}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

DataTable.propTypes = {
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.node.isRequired,
            render: PropTypes.func,
            align: PropTypes.oneOf(["left", "center", "right"]),
            /** Allow the cell to wrap instead of the default nowrap. */
            wrap: PropTypes.bool,
        })
    ).isRequired,
    rows: PropTypes.arrayOf(PropTypes.object),
    /** Per-row class hook, e.g. status tinting. */
    rowClassName: PropTypes.func,
    onRowClick: PropTypes.func,
    emptyCell: PropTypes.node,
};

DataTable.defaultProps = {
    rows: [],
    rowClassName: null,
    onRowClick: null,
    emptyCell: <EmptyValue />,
};

export default DataTable;
